import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import React, { useContext, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import {
  GuideViewVariant,
  ItemType,
  MessageTimestamp,
  MessageViewVariant
} from '../../../types/enums';
import type {
  GuidesQuery,
  GuidesQueryVariables,
  GuideViewFragment,
  ManualSortArticleListQuery,
  ManualSortArticleListQueryVariables,
  MessageViewFragment
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { ItemViewTypeAndProps } from '../../entities/types';
import manualSortArticleListQuery from './ManualSortArticleList.query.graphql';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import { GuideCardSize, GuideType } from '../enums';
import guideQuery from '../GuideSelectorComponent/Guides.query.graphql';
import type { GuideWidgetProps } from '../GuideView/type';
import useKnowledgeBaseProperties from '../useKnowledgeBaseProperties';
import localStyles from './GuidesWidget.module.pcss';

const defaultProps: GuideWidgetProps = {
  style: 'detailedList',
  listVariant: {
    type: ListVariant.UNSTYLED
  },
  viewVariant: {
    type: GuideViewVariant.INLINE,
    props: {
      useGuideTitle: true,
      useDescription: true,
      useIcon: true,
      clampDescriptionLines: 2
    }
  },
  messageViewVariant: {
    type: MessageViewVariant.INLINE,
    props: {
      useAuthorLogin: true,
      useBody: true,
      usePreviewMedia: false,
      useBoardIcon: true,
      useNode: false,
      useRepliesCount: true,
      useTags: true,
      timeStampType: MessageTimestamp.LAST_POSTING_ACTIVITY_TIME,
      useUnreadCount: true,
      useViewCount: true,
      useKudosCount: true,
      useAuthorRank: false,
      clampBodyLines: 3,
      useMedia: true,
      useNodeLink: true,
      useTextBody: true,
      useSpoilerFreeBody: true,
      clampSubjectLines: 2,
      renderPostTimeBeforeAuthor: true,
      useManualSortNav: true
    }
  },
  panelType: PanelType.STANDARD,
  useTitle: true,
  pageSize: 10,
  hideIfEmpty: true,
  pagerOption: true,
  textAlignment: TextAlignment.CENTER,
  cardSize: GuideCardSize.LG,
  pagerVariant: {
    type: PagerVariant.LOAD_MORE
  },
  leadIcon: 'icon',
  guideType: GuideType.STANDARD
};

export function getFinalProps(props: GuideWidgetProps): GuideWidgetProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}

/**
 * This component is used to render a list of guides nodes
 * @author Amit Agrawal
 */
const GuidesWidget: WidgetFC<GuideWidgetProps> = props => {
  const { isVisible, ...rest } = props;
  const finalProps: GuideWidgetProps = getFinalProps(rest);
  const {
    className,
    viewVariant,
    messageViewVariant,
    listVariant,
    panelType,
    useTitle,
    pageSize,
    hideIfEmpty,
    pagerVariant,
    guideType
  } = finalProps;
  const useEmpty = !hideIfEmpty;

  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.GUIDES_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;
  const { contextNode } = useContext(AppContext);
  const title = formatMessage('title');

  const variables: GuidesQueryVariables = {
    constraints: {
      nodeId: {
        eq: contextNode.id
      },
      includeEmpty: {
        eq: false
      }
    },
    useAvatar: true,
    first: pageSize
  };

  const { loading: kbPropertiesLoading, isGuidesAndChaptersEnabled } =
    useKnowledgeBaseProperties(module);

  const queryResult = useQueryWithTracing<GuidesQuery, GuidesQueryVariables>(module, guideQuery, {
    variables,
    skip: !isGuidesAndChaptersEnabled || guideType !== GuideType.STANDARD
  });

  const manualSortQueryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    ManualSortArticleListQuery,
    ManualSortArticleListQueryVariables
  >(module, ItemType.MESSAGE, messageViewVariant, manualSortArticleListQuery, {
    variables: {
      constraints: {
        boardId: { eq: contextNode.id }
      },
      first: pageSize
    },
    skip: !isGuidesAndChaptersEnabled || guideType !== GuideType.MANUAL_SORT
  });

  useEffect(() => {
    if (
      !useEmpty &&
      (!queryResult?.loading || !manualSortQueryResult?.loading) &&
      !textLoading &&
      !kbPropertiesLoading
    ) {
      if (isGuidesAndChaptersEnabled) {
        isVisible(
          guideType === GuideType.STANDARD
            ? queryResult?.data?.guides.totalCount !== 0
            : manualSortQueryResult?.data?.manuallyOrderedArticles.totalCount !== 0
        );
      } else {
        isVisible(false);
      }
    }
  }, [
    isVisible,
    useEmpty,
    textLoading,
    kbPropertiesLoading,
    isGuidesAndChaptersEnabled,
    queryResult,
    manualSortQueryResult,
    guideType
  ]);

  if (
    queryResult?.error ||
    manualSortQueryResult?.error ||
    textLoading ||
    !isGuidesAndChaptersEnabled
  ) {
    return null;
  }

  /**
   * function to render header for guide list
   */
  function renderHeader(): React.ReactElement {
    return <h3 className={cx('lia-list-header')}>{title}</h3>;
  }

  /**
   * A callback function to render Empty State when entity list has no content
   */
  function renderEmptyState(): React.ReactElement {
    const emptyDescription =
      guideType === GuideType.STANDARD
        ? formatMessage('guides.emptyDescription')
        : formatMessage('articles.emptyDescription');
    return <EmptyState alignment={TextAlignment.LEFT} description={emptyDescription} />;
  }

  return (
    <EditableWidget<GuideWidgetProps> props={finalProps}>
      {guideType === GuideType.STANDARD ? (
        <PaneledItemList<
          GuideViewFragment,
          ItemType.GUIDE,
          ItemViewTypeAndProps<ItemType.GUIDE, GuideViewVariant>,
          GuidesQuery,
          GuidesQueryVariables
        >
          type={ItemType.GUIDE}
          variant={viewVariant}
          queryResult={queryResult}
          pageSize={pageSize}
          listVariant={listVariant}
          empty={renderEmptyState}
          panel={panelType}
          className={cx(className)}
          bodyClassName={cx('lia-list-body')}
          data-testid="guideList"
          header={useTitle && renderHeader}
          headerClassName={cx('lia-g-pb-10')}
          useFooter={pagerVariant.type !== PagerVariant.NONE}
          pagerVariant={{
            ...pagerVariant,
            props: {
              title: formatMessage('loadMore')
            }
          }}
          useEmpty={useEmpty}
        />
      ) : (
        <PaneledItemList<
          MessageViewFragment,
          ItemType.MESSAGE,
          ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>,
          ManualSortArticleListQuery,
          ManualSortArticleListQueryVariables
        >
          type={ItemType.MESSAGE}
          variant={messageViewVariant}
          queryResult={manualSortQueryResult}
          pageSize={pageSize}
          listVariant={listVariant}
          empty={renderEmptyState}
          panel={panelType}
          className={cx(className)}
          bodyClassName={cx('lia-list-body')}
          data-testid="manualSortArticleList"
          header={useTitle && renderHeader}
          headerClassName={cx('lia-g-pb-10')}
          useFooter={pagerVariant.type !== PagerVariant.NONE}
          pagerVariant={pagerVariant}
          useEmpty={useEmpty}
        />
      )}
    </EditableWidget>
  );
};

export default GuidesWidget;
